import React from 'react'

// Константы из модели User (синхронизированы с backend)
export const USER_STATUS = {
  DELETED: 0,
  BLOCKED: 1,
  ACTIVE: 10,
} as const

export const USER_ROLE = {
  DEFAULT_USER: 1,
  COURIER: 2,
  ADMIN: 10,
  EXTERNAL_COURIER: 21,
} as const

export type UserStatus = typeof USER_STATUS[keyof typeof USER_STATUS]
export type UserRole = typeof USER_ROLE[keyof typeof USER_ROLE]

// === АВТОРИЗАЦИЯ АДМИНОВ ===

export interface AdminLoginRequest {
  username: string
  password: string
}

export interface AdminLoginResponse {
  access_token: string
  token_type: string
  user_id: number
}

export interface AdminUserResponse {
  user_id: number
  login?: string
  email?: string
  name?: string
  role: number
  is_active: boolean
  admin_role_id?: number | null
  role_name?: string | null
  is_superadmin?: boolean
  permissions?: string[]
}

// === ЛОГИ API ЗАПРОСОВ ===

export interface LogFileInfo {
  name: string
  size: number
  size_mb: number
  modified_at: string
  modified_at_formatted?: string
  is_current: boolean
}

export interface LogsListResponse {
  success: boolean
  logs: LogFileInfo[]
  total: number
}

export interface LogStatsResponse {
  success: boolean
  stats: {
    exists: boolean
    filename: string | null
    size: number
    size_mb?: number
    lines: number
    modified_at?: string
    error?: string
  }
}

export interface LogPreviewResponse {
  success: boolean
  filename: string
  max_lines: number
  content: string
}

// === УПРАВЛЕНИЕ ПОЛЬЗОВАТЕЛЯМИ ===

export interface UserListRequest {
  page?: number
  limit?: number
  status?: number | undefined
  role?: number | undefined
  search?: string | undefined
  region_id?: number | undefined
  transport_type_id?: number | undefined
  created_from?: number | undefined
  created_to?: number | undefined
}

export interface UserListItemResponse {
  user_id: number
  full_name: string
  email?: string
  phone?: string
  status: number
  status_text: string
  role: number
  role_text: string
  created_at_formatted: string
  region_name?: string
  admin_role_id?: number | null
}

export interface UserListResponse {
  items: UserListItemResponse[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface UserDetailsResponse {
  user_id: number
  login?: string
  email?: string
  phone?: string
  name?: string
  full_name?: string
  status: number
  status_text: string
  role: number
  role_text: string
  
  // Профильные данные курьера
  first_name?: string
  last_name?: string
  middle_name?: string
  inn?: string
  passport?: string
  driver_license?: string
  driver_license_current?: string
  driver_license_pending?: string
  driver_license_status?: string
  
  // Новые поля курьера
  delivery_radius?: number
  latitude?: number
  longitude?: number
  address?: string
  
  // Изображения
  profile_image_key?: string
  passport_image_first_key?: string
  passport_image_registration_key?: string
  driver_license_key?: string
  
  // Справочники (старые поля для совместимости)
  region_name?: string
  activity_type_name?: string
  transport_type_name?: string
  
  // Новые M2M связи
  activity_types?: ActivityTypeResponse[]
  /** Комментарий к виду деятельности «Прочее» (что именно перевозит курьер) */
  other_activity_comment?: string
  transport_types?: TransportTypeResponse[]
  transport_types_current?: TransportTypeResponse[]
  transport_types_pending?: TransportTypeResponse[]
  transport_types_status?: string

  // Модерация
  moderation_comment?: string
  moderation_fields?: string[]

  // Даты
  created_at_formatted: string
  updated_at_formatted: string

  // Base64 изображения для админ панели
  profile_image_base64?: string
  passport_image_first_base64?: string
  passport_image_registration_base64?: string
  driver_license_image_base64?: string
  driver_license_image_current_base64?: string
  driver_license_image_pending_base64?: string
}

export interface AdminUserCreateRequest {
  login: string
  email: string
  name: string
  password: string
  admin_role_id?: number | null
}

export interface AdminUserCreateResponse {
  user_id: number
  login: string
  email: string
  name: string
  admin_role_id?: number | null
}

export interface UpdateUserStatusRequest {
  status: number
  comment?: string
}

// Смена типа курьера (роль 2 — свой, 21 — сторонний)
export interface RoleUpdateRequest {
  role: number
  comment?: string
}

// === МОДЕРАЦИЯ КУРЬЕРОВ ===

export interface ModerationItemResponse {
  user_id: number
  full_name: string
  email?: string
  phone?: string
  inn?: string
  passport?: string
  driver_license?: string
  region_name?: string
  activity_type_name?: string
  transport_type_name?: string
  created_at_formatted: string
  
  // Текущий тип (роль) курьера
  role?: number
  role_text?: string

  // Поля для определения типа модерации
  moderation_type: string
  moderation_type_label: string
  change_request_id?: number
  new_driver_license?: string
  new_driver_image_key?: string

  // Base64 изображения для админ панели
  profile_image_base64?: string
  passport_image_first_base64?: string
  passport_image_registration_base64?: string
  driver_license_image_base64?: string

  // Статус модерации
  status_moderation?: number
  status_text?: string
  last_reject_reason?: string | null
}

export interface ModerationListResponse {
  items: ModerationItemResponse[]
  total: number
  page: number
  limit: number
  pages: number
}

// Фильтр списка модерации по статусу
export type ModerationStatusFilter = 'on_moderation' | 'rejected' | 'approved'

// Параметры запроса списка модерации (все фильтры необязательны)
export interface ModerationListRequest {
  page?: number
  limit?: number
  search?: string | undefined
  moderation_type?: string | undefined
  status?: ModerationStatusFilter | undefined
  name?: string | undefined
  email?: string | undefined
  phone?: string | undefined
  region_id?: number | undefined
  transport_type_id?: number | undefined
  created_from?: number | undefined
  created_to?: number | undefined
}

export interface ModerationActionRequest {
  action: 'approve' | 'reject'
  comment?: string
  reject_fields?: string[]
  // Тип курьера при approve: 2 — штатный, 21 — сторонний
  role?: number
  // Ожидаемый статус модерации для защиты от гонок (409)
  expected_status_moderation?: number
}

export interface ModerationActionResponse {
  success: boolean
  message: string
  new_status: number
}

export interface ModerationHistoryItem {
  created_at: number
  action: 'created' | 'approved' | 'rejected' | 'reopened' | string
  old_status?: number | null
  new_status?: number | null
  reason?: string | null
  admin_user_id?: number | null
  admin_name?: string | null
}

export interface ModerationHistoryResponse {
  items: ModerationHistoryItem[]
}

// === СПРАВОЧНИКИ ===

export interface DictionaryResponse {
  id: number
  name: string
  is_active?: boolean
  sort_order?: number
}

export interface RegionResponse {
  id: number
  name: string
  full_name?: string
  fias?: string
  longitude?: number
  latitude?: number
  kladr?: string
  is_active?: boolean
  sort_order?: number
}

// Запросы для CRUD операций со справочниками
export interface RegionCreateRequest {
  name: string
  full_name?: string
  fias?: string
  longitude?: number
  latitude?: number
  kladr?: string
}

export interface RegionUpdateRequest {
  name?: string
  full_name?: string
  fias?: string
  longitude?: number
  latitude?: number
  kladr?: string
  is_active?: boolean
}

export interface DictionaryItemCreateRequest {
  name: string
  sort_order?: number
}

export interface DictionaryItemUpdateRequest {
  name?: string
  is_active?: boolean
  sort_order?: number
}

// === НОВЫЕ СПРАВОЧНИКИ (ВИДЫ ДЕЯТЕЛЬНОСТИ И ТРАНСПОРТА) ===

// Виды деятельности
export interface ActivityTypeResponse {
  activity_type_id: number
  name: string
  is_active: boolean
  sort_order: number
  allows_multiselect: boolean
}

export interface ActivityTypeListResponse {
  items: ActivityTypeResponse[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface ActivityTypeCreateRequest {
  name: string
  is_active?: boolean
  sort_order?: number
  allows_multiselect?: boolean
}

export interface ActivityTypeUpdateRequest {
  name?: string
  is_active?: boolean
  sort_order?: number
  allows_multiselect?: boolean
}

// Виды транспорта
export interface TransportTypeResponse {
  transport_type_id: number
  name: string
  is_active: boolean
  sort_order: number
  requires_license: boolean
}

export interface TransportTypeListResponse {
  items: TransportTypeResponse[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface TransportTypeCreateRequest {
  name: string
  is_active?: boolean
  sort_order?: number
  requires_license?: boolean
}

export interface TransportTypeUpdateRequest {
  name?: string
  is_active?: boolean
  sort_order?: number
  requires_license?: boolean
}

// Обновленные типы для курьеров с M2M связями
export interface CourierDetailResponse {
  user_id: number
  first_name?: string
  last_name?: string
  middle_name?: string
  email?: string
  phone?: string
  inn?: string
  passport?: string
  driver_license?: string
  region_id?: number
  delivery_radius?: string
  latitude?: string
  longitude?: string
  status_moderation?: number
  moderation_comment?: string
  moderation_fields?: string[]
  
  // M2M связи
  activity_types: ActivityTypeResponse[]
  transport_types: TransportTypeResponse[]
}

export interface CourierUpdateRequest {
  first_name?: string
  last_name?: string
  middle_name?: string
  email?: string
  inn?: string
  passport_series?: string
  passport_number?: string
  region_id?: number
  delivery_radius?: string
  latitude?: string
  longitude?: string
  status_moderation?: number
  
  // M2M связи
  activity_type_ids?: number[]
  transport_type_ids?: number[]
}

// === FAQ (ЧАСТЫЕ ВОПРОСЫ) ===

export interface FAQResponse {
  faq_id: number
  title: string
  description: string
  sort_order: number
  is_active: boolean
  created_at_formatted: string
  updated_at_formatted: string
}

export interface FAQListResponse {
  items: FAQResponse[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface FAQCreateRequest {
  title: string
  description: string
  sort_order?: number
}

export interface FAQUpdateRequest {
  title?: string
  description?: string
  sort_order?: number
  is_active?: boolean
}

// === ОБЩИЕ ТИПЫ ===

export interface SuccessResponse {
  success: boolean
  message: string
}

// Пагинация (общие типы)
export interface PaginationParams {
  page: number
  limit: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
  pages: number
}

// Фильтры для пользователей
export interface UserFilters {
  status?: UserStatus
  role?: UserRole
  search?: string
}

// === ТИПЫ ДЛЯ UI КОМПОНЕНТОВ ===

export interface Column<T> {
  key: keyof T | 'actions'
  title: string
  width?: string
  /** Иконка вместо текста в узком заголовке: текст уходит в aria-label и подсказку */
  headerIcon?: React.ReactNode
  render?: (value: any, item: T) => React.ReactNode
}

// === ЮРИДИЧЕСКИЕ ЛИЦА ===

export interface LegalEntityListItem {
  legalEntityId: number
  name: string
  inn: string
  status: number
  statusText: string
  contactPerson?: string
  phone?: string
  email?: string
  createdAtFormatted: string
}

export interface LegalEntityListResponse {
  items: LegalEntityListItem[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface LegalEntityListRequest {
  page?: number
  limit?: number
  status?: number
  search?: string
}

export interface LegalEntityDetails extends LegalEntityListItem {
  externalId?: string
  fullName?: string
  kpp?: string
  ogrn?: string
  legalAddress?: string
  bankName?: string
  bankAccount?: string
  bankBik?: string
  rejectionReason?: string
  disableReason?: string
  updatedAtFormatted?: string
  approvedAtFormatted?: string
  disabledAtFormatted?: string
}

export interface LegalEntityActionResponse {
  success: boolean
  message: string
  status: number
  statusText: string
}

// === РОЛИ АДМИНКИ (F2) ===

export interface RolePermissionItem {
  section: string
  action: string
}

export interface AdminRole {
  admin_role_id: number
  name: string
  description?: string | null
  is_system: boolean
  permissions: RolePermissionItem[]
  users_count: number
}

export interface AdminRoleListResponse {
  items: AdminRole[]
  total: number
}

export interface CatalogActionItem {
  key: string
  label: string
}

export interface CatalogSectionItem {
  key: string
  label: string
  actions: CatalogActionItem[]
}

export interface AdminRoleCatalogResponse {
  sections: CatalogSectionItem[]
}

export interface AdminRoleWriteRequest {
  name: string
  description?: string | null
  permissions: string[]
}

// === ТАРИФЫ КУРЬЕРОВ ===

export interface SurchargeDTO {
  courier_tariff_surcharge_id?: number
  condition_type: number
  condition_operator: number
  condition_value: string | null
  amount: number
}

export interface DistanceRuleDTO {
  /** Километры, включённые в базовую ставку. */
  included_km: number
  /** Надбавка за каждый следующий километр, ₽. */
  price_per_km: number
}

export interface OrderCountRuleDTO {
  courier_tariff_order_count_rule_id?: number
  threshold: number
  period: number
  amount: number
}

export interface CoefficientDTO {
  courier_tariff_coefficient_id?: number
  condition_type: number
  coefficient: number
  priority: number
  time_from: number | null
  time_to: number | null
  weekdays: string | null
  date_start_month: number | null
  date_start_day: number | null
  date_end_month: number | null
  date_end_day: number | null
}

export interface TariffVersionDTO {
  courier_tariff_version_id: number
  version_number: number
  base_rate: number
  min_amount: number | null
  max_amount: number | null
  period_from: number | null
  period_to: number | null
  status: number
  distance_rule: DistanceRuleDTO | null
  surcharges: SurchargeDTO[]
  order_count_rules: OrderCountRuleDTO[]
  coefficients: CoefficientDTO[]
}

export interface TariffDetailsDTO {
  tariff: {
    courier_tariff_id: number
    name: string
    description: string | null
    scope: number
    courier_id: number | null
    status: number
  }
  versions: TariffVersionDTO[]
}

export interface TariffListItemDTO {
  courier_tariff_id: number
  name: string
  scope: number
  courier_id: number | null
  status: number
  base_rate: number | null
  version_number: number | null
}

export interface TariffListResponse {
  items: TariffListItemDTO[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface LedgerItemDTO {
  courier_ledger_entry_id: number
  courier_id: number
  shop_order_id: number | null
  entry_type: number
  amount: number
  parent_entry_id: number | null
  basis: string | null
  reason: string | null
  actor_id: number | null
  created_at: number
  created_at_formatted: string | null
}

export interface LedgerListResponse {
  items: LedgerItemDTO[]
  total: number
  page: number
  limit: number
  pages: number
}

// Заказы без настроенного тарифа (проблемные начисления)
export interface ProblemOrderDTO {
  shop_order_id: number
  number: string | null
  courier_id: number | null
  courier_name: string | null
  status: number
  reason: string
}

export interface ProblemOrdersResponse {
  items: ProblemOrderDTO[]
  total: number
}

export interface PreviewResponse {
  tariff_id: number
  tariff_name: string
  version_id: number
  version_number: number
  base_rate: number
  surcharges_total: number
  distance_km: number | null
  distance_surcharge: number
  order_count_bonus: number
  fixed_amount: number
  coefficient: number
  chosen_coefficient_id: number | null
  priority: number | null
  total: number
  coefficients: Array<{
    coefficient_id: number
    condition_type: number
    priority: number
    coefficient: number
    matched: boolean
    chosen: boolean
  }>
}

export interface CourierTariffSummary {
  tariff: { mode: string; tariff_id: number | null; name: string }
  accrued_today: number
  accrued_period: number
  operations_count: number
}

// === ТАРИФЫ ЮРЛИЦ (КОНСТРУКТОР) ===

export interface ClientTariffRuleDTO {
  id: number
  param: number
  operator: number
  value: string
  label: string
  amount: number
  enabled: boolean
}

export interface ClientTariffDTO {
  id: number
  name: string
  description: string | null
  kind: number
  region_id: number
  legal_entity_id: number | null
  status: number
  date_from: number | null
  date_to: number | null
  base_price: string
  rules: ClientTariffRuleDTO[]
  created_at: number | null
  updated_at: number | null
}

export interface ClientTariffListItemDTO {
  id: number
  name: string
  kind: number
  region_id: number
  status: number
  date_from: number | null
  date_to: number | null
  base_price: string
  rules_count: number
}

export interface ClientTariffListResponse {
  items: ClientTariffListItemDTO[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface ClientTariffRuleWrite {
  param: number
  operator: number
  value: string
  label: string
  amount: number
  enabled: boolean
}

export interface ClientTariffWriteRequest {
  name: string
  description?: string | null
  kind: number
  regionId: number
  legalEntityId?: number | null
  dateFrom?: number | null
  dateTo?: number | null
  basePrice: number
  rules: ClientTariffRuleWrite[]
}

export interface ClientTariffPreviewRequest {
  legalEntityId: number
  regionId: number
  deliveryType?: string
  interval?: string
  origin?: string
}

export interface ClientTariffPreviewResponse {
  result: boolean
  data: {
    tariff_id: number
    name: string
    kind: number
    base: string
    applied: { label: string; amount: string }[]
    total: string
  } | null
  error: string | null
}

// === ЗАКАЗЫ (ADMIN, READ-ONLY) ===
// Поля повторяют camelCase-алиасы backend-схем (app/api/admin_orders/schemas.py).
// Даты — Unix-секунды; *Formatted — подписи из backend по Москве.

// Интервал доставки: секунды от полуночи и подпись `HH:MM - HH:MM`
export interface OrderDeliveryInterval {
  fromValue: number
  toValue: number
  label: string
}

// Строка списка заказов
export interface OrderListItem {
  shopOrderId: number
  number: string | null
  externalOrderId: string | null
  legalEntityId: number | null
  legalEntityName: string | null
  source: string | null
  sourceLabel: string | null
  deliveryDate: number | null
  deliveryDateFormatted: string | null
  deliveryInterval: OrderDeliveryInterval | null
  address: string | null
  recipientName: string | null
  recipientPhone: string | null
  status: number | null
  statusText: string | null
  externalStatus: string | null
  externalStatusName: string | null
  deliveryType: string | null
  deliveryTypeName: string | null
  courierId: number | null
  courierName: string | null
  courierPhone: string | null
  clientPrice: string | null
  courierReward: string | null
  margin: string | null
  comment: string | null
  createdAt: number | null
  createdAtFormatted: string | null
  isClosed: boolean
}

// Страница списка заказов
export interface OrderListResponse {
  items: OrderListItem[]
  total: number
  page: number
  limit: number
  pages: number
}

// Параметры списка заказов (camelCase для UI).
// dateFrom/dateTo — Unix-секунды начала дня (МСК), включительно.
// Преобразование в snake_case выполняет AdminOrderService.getOrders.
export interface OrderListRequest {
  page?: number | undefined
  limit?: number | undefined
  // active — в работе, closed — выполненные, all — все
  tab?: string | undefined
  status?: number | undefined
  // courier — доставка курьером, third_party — сторонний курьер
  deliveryType?: string | undefined
  legalEntityId?: number | undefined
  courierId?: number | undefined
  search?: string | undefined
  // delivery — по дате доставки, creation — по дате создания
  dateType?: string | undefined
  dateFrom?: number | undefined
  dateTo?: number | undefined
}

// Склад отправления
export interface OrderStorage {
  storageId: number | null
  address: string | null
  number: number | null
  latitude: number | null
  longitude: number | null
}

// Отправитель: юрлицо и адрес склада
export interface OrderSender {
  name: string | null
  contactPerson: string | null
  phone: string | null
  address: string | null
}

// Получатель заказа
export interface OrderRecipient {
  name: string | null
  phone: string | null
  address: string | null
  latitude: number | null
  longitude: number | null
}

// Позиция состава заказа
export interface OrderCompositionItem {
  name: string | null
  count: number | null
}

// Физическая единица товара (ячейка/QR)
export interface OrderGood {
  name: string | null
  address: string | null
  qr: string | null
}

// Зафиксированный расчёт вознаграждения курьера
export interface OrderCourierTariff {
  courierTariffId: number | null
  courierTariffVersionId: number | null
  amount: string | null
  // Произвольный JSON-массив из снимка тарифа
  breakdown: unknown[]
  frozenAt: number | null
  frozenAtFormatted: string | null
}

// Событие истории изменений заказа
export interface OrderHistoryItem {
  orderChangeHistoryId: number | null
  eventType: string | null
  eventLabel: string | null
  fieldName: string | null
  oldValue: string | null
  newValue: string | null
  actorType: string | null
  actorTypeLabel: string | null
  actorId: number | null
  actorName: string | null
  operationId: string | null
  comment: string | null
  createdAt: number | null
  createdAtFormatted: string | null
}

// История изменений заказа
export interface OrderHistoryResponse {
  items: OrderHistoryItem[]
}

// Карточка заказа: строка списка + склад, стороны, состав и тариф
export interface OrderDetails extends OrderListItem {
  storage: OrderStorage | null
  sender: OrderSender
  recipient: OrderRecipient
  items: OrderCompositionItem[]
  goods: OrderGood[]
  paymentTypeName: string | null
  courierTariff: OrderCourierTariff | null
  history: OrderHistoryItem[]
}

// Вариант фильтра: значение и подпись
export interface OrderFilterOption {
  value: number | string
  label: string
}

// Юрлицо в фильтре списка заказов
export interface OrderLegalEntityOption {
  legalEntityId: number
  name: string
}

// Курьер в фильтре списка заказов
export interface OrderCourierOption {
  userId: number
  name: string | null
  phone: string | null
  externalUserId: string | null
}

// Справочники фильтров списка заказов
export interface OrderFiltersResponse {
  statuses: OrderFilterOption[]
  deliveryTypes: OrderFilterOption[]
  legalEntities: OrderLegalEntityOption[]
  couriers: OrderCourierOption[]
  sources: OrderFilterOption[]
}

// Курьер с нагрузкой на выбранную дату
export interface OrderCourier {
  userId: number
  name: string | null
  phone: string | null
  role: number | null
  externalUserId: string | null
  status: number | null
  loadTotal: number
  loadActive: number
}

// Список курьеров с нагрузкой
export interface OrderCouriersResponse {
  items: OrderCourier[]
}

// Короткая строка заказа на экране нагрузки курьера
export interface CourierLoadOrder {
  shopOrderId: number
  number: string | null
  deliveryDateFormatted: string | null
  deliveryInterval: OrderDeliveryInterval | null
  address: string | null
  status: number | null
  statusText: string | null
}

// Нагрузка курьера на дату: сводка и список заказов
export interface CourierLoadResponse {
  courierId: number
  courierName: string | null
  date: number
  total: number
  activeTotal: number
  orders: CourierLoadOrder[]
}

// === ВЕРСИИ ПРИЛОЖЕНИЯ (АДМИНКА) ===
// Поля повторяют snake_case-DTO backend (app/api/app_versions/schemas.py).

export type AppPlatform = 'android' | 'ios'
export type AppUpdateType = 'off' | 'recommended' | 'forced'

// Текущие настройки обновления одной платформы
export interface AppUpdateSettings {
  platform: AppPlatform
  is_active: boolean
  update_type: AppUpdateType
  min_version: string
  latest_version: string
  store_url: string
  forced_title: string
  forced_message: string
  recommended_title: string
  recommended_message: string
  updated_at: number | null
  updated_by_name: string
}

// Полное сохранение настроек платформы (без служебных полей)
export type AppVersionUpdateRequest = Omit<
  AppUpdateSettings,
  'platform' | 'updated_at' | 'updated_by_name'
>

export interface AppVersionsResponse {
  items: AppUpdateSettings[]
}

// Изменённое поле в записи истории
export interface AppVersionHistoryChange {
  field: string
  old_value: string
  new_value: string
}

export interface AppVersionHistoryItem {
  id: number
  platform: AppPlatform
  action: 'saved' | 'activated' | 'deactivated'
  admin_user_id: number | null
  admin_name: string
  created_at: number
  changes: AppVersionHistoryChange[]
}

export interface AppVersionHistoryResponse {
  items: AppVersionHistoryItem[]
}

// === ДЕМО-БАЛАНС ЮРЛИЦА (app/api/admin_balance/schemas.py) ===
// Суммы приходят строками (`'9500.00'`), даты — строками `dd.mm.yyyy HH:MM`;
// приводить суммы к числу для отображения нельзя.

/** Снимок баланса компании: общий, зарезервированный и доступный. */
export interface BalancesDTO {
  total: string
  reserved: string
  available: string
  demoEnabled: boolean
}

/** Счёт на пополнение демо-баланса. */
export interface InvoiceDTO {
  invoiceId: number
  number?: string | null
  amount: string
  status: number
  statusName: string
  source: number
  comment?: string | null
  legalEntityId: number
  entityName?: string | null
  createdAt?: string | null
  paidAt?: string | null
  cancelledAt?: string | null
  cancelReason?: string | null
  pdfUrl: string
}

export interface InvoiceListResponse {
  items: InvoiceDTO[]
  total: number
  page: number
  limit: number
  pages: number
}

/** Фильтры списка счетов; на бэкенд уходят snake_case-параметрами. */
export interface InvoiceListFilters {
  page?: number
  limit?: number
  status?: number
  legalEntityId?: number
  search?: string
  dateFrom?: number
  dateTo?: number
}

export interface InvoiceCreateRequest {
  /** Сумма строкой: бэкенд принимает Decimal, от 10.00 до 1000000.00 */
  amount: string
  comment?: string
}

/** Запись журнала баланса: пополнение, резерв, списание, освобождение. */
export interface LedgerOperationDTO {
  entryId: number
  entryType: number
  entryTypeName: string
  amount: string
  totalDelta: string
  reservedDelta: string
  invoiceId?: number | null
  invoiceNumber?: string | null
  shopOrderId?: number | null
  orderNumber?: string | null
  basis?: string | null
  actorId?: number | null
  createdAt?: string | null
}

export interface OperationListResponse {
  items: LedgerOperationDTO[]
  total: number
  page: number
  limit: number
  pages: number
}

/** Активный резерв под заказ — его можно освободить вручную. */
export interface HoldDTO {
  entryId: number
  legalEntityId: number
  shopOrderId?: number | null
  amount: string
  basis?: string | null
  createdAt?: string | null
}

export interface HoldListResponse {
  items: HoldDTO[]
}

export interface BalanceSettingsResponse {
  legalEntityId: number
  demoEnabled: boolean
}

// === ПУШ-РАССЫЛКИ (АДМИНКА) ===
// Поля повторяют camelCase-DTO backend (app/api/admin_panel/schemas.py).

/** Аудитория рассылки: все, штатные курьеры или внешние партнёры. */
export type PushAudience = 'all' | 'staff' | 'external'

/** Тело запроса отправки рассылки. */
export interface PushCampaignRequest {
  title: string
  message: string
  audience: PushAudience
}

export interface PushCampaignResponse {
  success: boolean
  campaignId: number
  recipientsCount: number
}

export interface PushAudienceCountResponse {
  audience: PushAudience
  count: number
}

export interface PushHistoryItem {
  kind: 'campaign' | 'system'
  id: number
  createdAt: number | null
  title: string
  message: string
  audience: PushAudience | 'personal' | 'topic'
  audienceLabel: string
  recipientsCount: number | null
  status: 'sent' | 'error'
  sourceCampaignId: number | null
}

export interface PushHistoryResponse {
  items: PushHistoryItem[]
  total: number
  page: number
  limit: number
  pages: number
}
